import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { CampaignResults } from './CampaignResults';
import { BudgetPreviewItem, CampaignResult, SettingsForm, TemplateCampaignFormProps } from './types';
import { Settings } from './Settings';
import {
  useCreateMultipleCampaignMutation,
  usePreviewMultipleCampaignBudgetMutation,
  useCheckDomainsMutation,
} from '~/features/accounts/model/api/accounts-api';
import { useSnackbar } from 'notistack';
import { isValidDomain } from '~/shared/utils/common';
import { useTemplateCampaignQuery } from '~/features/templates/model/hooks/useTemplateCampaignQuery';
import { useSelector } from 'react-redux';
import { usersApi } from '~/features/user/model/api/users-api';
import { buildFinalUrlSuffixPreview } from '~/shared/utils/buildFinalUrlSuffixPreview';

// Оставляем ту же базовую автологику, что и на backend: берем имя из домена,
// но дальше пользователь может свободно переопределить его вручную.
const buildBusinessNameFromDomain = (value: string) => {
  const cleanedValue = value.trim().replace(/^https?:\/\//, '');
  if (!cleanedValue) {
    return '';
  }

  const host = cleanedValue.split('/')[0];
  const parts = host.split('.');
  const businessName = parts.length > 2 ? parts[1] : parts[0];

  return businessName.slice(0, 25);
};

// Для массового запуска храним Business name отдельно по каждому домену.
// Это позволяет сохранить автозаполнение и при этом редактировать бренды независимо.
const buildBusinessNamesByDomain = (domains: string[], currentValues: Record<string, string>) => {
  return domains.reduce<Record<string, string>>((acc, domain) => {
    const currentValue = currentValues[domain];

    acc[domain] =
      typeof currentValue === 'string' && currentValue.trim() !== ''
        ? currentValue
        : buildBusinessNameFromDomain(domain);

    return acc;
  }, {});
};

export const TemplateCampaignForm: React.FC<TemplateCampaignFormProps> = ({
  selectedRows,
  onCancel,
  onCreate,
}) => {

  const { data: me } = useSelector(usersApi.endpoints.getUserInfo.select(undefined)) as any;

  const [domainsValue, setDomainsValue] = useState('');
  const [domains, setDomains] = useState<string[]>([]);
  const [activeStep, setActiveStep] = useState(0);
  const [template, setTemplate] = useState(null);

  const [results, setResults] = useState<CampaignResult[]>([]);
  const [budgetPreviewItems, setBudgetPreviewItems] = useState<BudgetPreviewItem[]>([]);
  const [images, setImages] = useState([]);
  const [settings, setSettings] = useState<SettingsForm>({
    business_names: {},
    append_tag: true,
    custom_utm: '',
    final_url_suffix: '',
    headlines: { min: 1, max: 1 },
    descriptions: { min: 1, max: 1 },
    logo: { min: 1, max: 1 },
    mode: 'marketing_image',
    marketing_image: { min: 1, max: 1 },
    marketing_link: { min: 1, max: 1 },
  });


  const { enqueueSnackbar } = useSnackbar();
  const { data: templateData, error, isFetching } = useTemplateCampaignQuery(me?.roles, {
    page: 1,
    pageSize: 1000,
    search: null,
  });

  const [templateCampaign, { isLoading }] = useCreateMultipleCampaignMutation();
  const [previewMultipleCampaignBudget, { isLoading: isPreviewLoading }] = usePreviewMultipleCampaignBudgetMutation();
  const [checkDomains, { isLoading: isCheckingDomains }] = useCheckDomainsMutation();

  const theme = useTheme();

  const previewCustomerId = useMemo(() => {
    return String(selectedRows[0]?.customer_id || '').replace(/\D/g, '');
  }, [selectedRows]);

  const previewAccountName = useMemo(() => {
    return selectedRows[0]?.name || '';
  }, [selectedRows]);

  const suffixPreview = useMemo(() => {
    return buildFinalUrlSuffixPreview({
      customerId: previewCustomerId,
      customerIdPlaceholder: '123-456-7890',
      appendTag: settings.append_tag,
      customUtm: settings.custom_utm,
      finalUrlSuffix: settings.final_url_suffix,
    });
  }, [previewCustomerId, settings.append_tag, settings.custom_utm, settings.final_url_suffix]);

  const suffixPreviewHint = useMemo(() => {
    if (selectedRows.length === 1) {
      return 'Показан готовый итоговый suffix для выбранного аккаунта с визуальной маской Customer ID.';
    }

    if (selectedRows.length > 1 && previewAccountName) {
      return `Показан готовый итоговый suffix для первого выбранного аккаунта: ${previewAccountName}. Для остальных аккаунтов строка будет такой же, но со своим Customer ID.`;
    }

    return 'Показан готовый итоговый suffix. При отправке в API Customer ID уходит без дефисов.';
  }, [previewAccountName, selectedRows.length]);

  const activeTemplate = useMemo(() => {
    const filtered = templateData?.data?.filter(item => item.name === template);

    if (!filtered) {
      return {
        id: 0,
        headlines: 0,
        descriptions: 0,
        logo: 0,
        budget_usd_from: 1,
        budget_usd_to: 1,
        budget_base_currency: 'USD',
        marketing_image: 0,
        marketing_image_square: 0,
        marketing_image_landscape: 0,
        marketing_image_portrait: 0,
        marketing_link: 0,
        append_tag: false,
        custom_utm: '',
        final_url_suffix: '',
        isImage: false,
      };
    }

    const sourceImageCount =
      filtered[0]?.data?.source_image?.length ?? 0;
    const selectedMarketingFormats = Array.isArray(filtered[0]?.data?.marketing_image_formats)
      ? filtered[0].data.marketing_image_formats
      : [];
    const generatedLandscapeCount = sourceImageCount * (selectedMarketingFormats.includes('landscape') ? 1 : 0);
    const generatedSquareCount = sourceImageCount * (selectedMarketingFormats.includes('square') ? 1 : 0);
    const generatedPortraitCount = sourceImageCount * (selectedMarketingFormats.includes('portrait') ? 1 : 0);
    const marketingLandscapeCount =
      filtered[0]?.data?.marketing_image_landscape?.length ?? generatedLandscapeCount;
    const marketingSquareCount =
      filtered[0]?.data?.marketing_image_square?.length ?? generatedSquareCount;
    const marketingPortraitCount =
      filtered[0]?.data?.marketing_image_portrait?.length ?? generatedPortraitCount;
    const legacyMarketingCount = marketingLandscapeCount
      ? 0
      : filtered[0]?.data?.marketing_image?.length ?? 0;
    const marketingImageCount =
      marketingLandscapeCount +
      marketingSquareCount +
      marketingPortraitCount +
      legacyMarketingCount;
    const hasRequiredImages =
      marketingLandscapeCount + marketingSquareCount + legacyMarketingCount > 0;
    const rawBudgetFrom = Number(filtered[0]?.data?.budget_usd_from);
    const rawBudgetTo = Number(filtered[0]?.data?.budget_usd_to);
    const legacyBudgets = Array.isArray(filtered[0]?.data?.budget)
      ? filtered[0].data.budget.filter((value: unknown) => Number.isFinite(Number(value))).map((value: unknown) => Number(value))
      : [];
    const budgetUsdFrom = Number.isFinite(rawBudgetFrom) && rawBudgetFrom >= 1
      ? rawBudgetFrom
      : (legacyBudgets.length ? Math.min(...legacyBudgets) : 1);
    const budgetUsdTo = Number.isFinite(rawBudgetTo) && rawBudgetTo >= budgetUsdFrom
      ? rawBudgetTo
      : (legacyBudgets.length ? Math.max(...legacyBudgets) : budgetUsdFrom);

    return {
      id: filtered[0]?.id,
      headlines: filtered[0]?.data?.headlines?.length || 1,
      descriptions: filtered[0]?.data?.descriptions?.length || 1,
      logo: filtered[0]?.data?.logo?.length || 1,
      budget_usd_from: budgetUsdFrom,
      budget_usd_to: budgetUsdTo,
      budget_base_currency: 'USD',
      marketing_image: marketingImageCount || 1,
      marketing_image_square: marketingSquareCount,
      marketing_image_landscape: marketingLandscapeCount + legacyMarketingCount,
      marketing_image_portrait: marketingPortraitCount,
      marketing_link: filtered[0]?.data?.marketing_link?.length || 1,
      append_tag: filtered[0]?.data?.append_tag ?? true,
      custom_utm: filtered[0]?.data?.custom_utm || '',
      final_url_suffix: filtered[0]?.data?.final_url_suffix || '',
      isImage: hasRequiredImages,
    };
  }, [template, templateData]);

  const parseDomains = useCallback((value: string) => {
    return value
      .split('\n')
      .map(v =>
        v
          .trim()
          .replace('https://', '')
          .replace('http://', ''),
      )
      .filter(Boolean)
      .filter(isValidDomain);
  }, []);

  const handleGenerateCampaign = useCallback(() => {
    // Simple round-robin assignment of domains to accounts
    const newResults: CampaignResult[] = [];
    const domainCount = domains.length;

    selectedRows.forEach((row, index) => {
      const domainIndex = index % domainCount;

      newResults.push({
        account: {
          id: row.id,
          name: row.name,
        },
        domain: {
          id: domainIndex,
          name: domains[domainIndex],
        },
      });
    });

    return newResults;
  }, [domains, selectedRows]);

  useEffect(() => {
    setSettings(prev => ({
      ...prev,
      append_tag: activeTemplate.append_tag,
      custom_utm: activeTemplate.custom_utm,
      final_url_suffix: activeTemplate.final_url_suffix || '',
      headlines: { min: 1, max: Math.min(activeTemplate.headlines, 5) },
      descriptions: { min: 1, max: Math.min(activeTemplate.descriptions, 5) },
      logo: { min: 1, max: Math.min(activeTemplate.logo, 5) },
      marketing_image: { min: 1, max: Math.min(activeTemplate.marketing_image, 5) },
      marketing_link: { min: 1, max: Math.min(activeTemplate.marketing_link, 5) },
      mode: activeTemplate.isImage ? 'marketing_image' : 'marketing_link',
    }));
  }, [activeTemplate.id]);

  useEffect(() => {
    setBudgetPreviewItems([]);
    setResults([]);
  }, [template, domainsValue, activeTemplate.id, settings]);

  useEffect(() => {
    setSettings(prev => {
      const nextBusinessNames = buildBusinessNamesByDomain(domains, prev.business_names || {});
      const currentBusinessNames = prev.business_names || {};

      const hasSameKeys =
        Object.keys(currentBusinessNames).length === Object.keys(nextBusinessNames).length &&
        Object.keys(nextBusinessNames).every(key => currentBusinessNames[key] === nextBusinessNames[key]);

      if (hasSameKeys) {
        return prev;
      }

      return {
        ...prev,
        business_names: nextBusinessNames,
      };
    });
  }, [domains]);

  const steps = ['Выбор шаблона', 'Настройки', 'Просмотр результата'];

  const validateSettings = useCallback(() => {
    const errors: string[] = [];
    if (!activeTemplate.id) {
      errors.push('Выберите шаблон');
    }

    if (settings.headlines.min > activeTemplate.headlines) {
      errors.push('В шаблоне недостаточно заголовков');
    }
    if (settings.descriptions.min > activeTemplate.descriptions) {
      errors.push('В шаблоне недостаточно описаний');
    }
    const uploadedLogos = Array.isArray(images)
      ? images.filter(Boolean).length
      : 0;
    const availableLogos = Math.max(activeTemplate.logo, uploadedLogos);
    if (settings.logo.min > availableLogos) {
      errors.push('Недостаточно логотипов для выбранного количества');
    }

    if (settings.mode === 'marketing_image') {
      const requiredCount =
        activeTemplate.marketing_image_landscape +
        activeTemplate.marketing_image_square;
      if (requiredCount === 0) {
        errors.push(
          'Добавьте минимум одно изображение 1:1 или 1.91:1 в шаблон',
        );
      }
      if (settings.marketing_image.min > activeTemplate.marketing_image) {
        errors.push('В шаблоне недостаточно маркетинговых изображений');
      }
    } else {
      if (settings.marketing_link.min > activeTemplate.marketing_link) {
        errors.push('В шаблоне недостаточно маркетинговых ссылок');
      }
    }

    if (errors.length) {
      enqueueSnackbar(errors.join('. '), {
        variant: 'error',
        autoHideDuration: 4000,
        style: { maxWidth: '700px' },
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      return false;
    }

    return true;
  }, [activeTemplate, enqueueSnackbar, settings, images]);

  const validateDomains = useCallback(() => {
    const hasInput = domainsValue.trim().length > 0;
    if (!template) {
      enqueueSnackbar('Выберите шаблон', {
        variant: 'error',
        autoHideDuration: 3000,
        style: { maxWidth: '600px' },
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      return false;
    }
    if (!domains.length) {
      enqueueSnackbar(
        hasInput
          ? 'Нет валидных доменов. Проверьте формат.'
          : 'Добавьте домены',
        {
          variant: 'error',
          autoHideDuration: 3000,
          style: { maxWidth: '600px' },
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        },
      );
      return false;
    }
    return true;
  }, [domains.length, domainsValue, enqueueSnackbar, template]);

  const checkDomainsAvailability = useCallback(async () => {
    const parsedDomains = parseDomains(domainsValue);
    if (!parsedDomains.length) {
      enqueueSnackbar('Добавьте домены', {
        variant: 'error',
        autoHideDuration: 3000,
        style: { maxWidth: '600px' },
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      return false;
    }
    try {
      const response: any = await checkDomains({ domains: parsedDomains }).unwrap();
      const results = response?.data ?? response ?? [];
      const validDomains = results
        .filter((item: any) => item?.status === 'ok' && item?.domain)
        .map((item: any) => item.domain);
      const invalidDomains = results.filter((item: any) => item?.status !== 'ok');

      if (!validDomains.length) {
        enqueueSnackbar('Все домены недоступны. Проверьте DNS/HTTP.', {
          variant: 'error',
          autoHideDuration: 4000,
          style: { maxWidth: '700px' },
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
        return false;
      }

      if (invalidDomains.length) {
        const statusLabel = (status: string) => {
          if (status === 'dns_fail') return 'DNS не найден';
          if (status === 'http_fail') return 'HTTP недоступен';
          if (status === 'invalid_format') return 'Неверный формат';
          return 'Недоступен';
        };
        const details = invalidDomains
          .map((item: any) => `${item?.domain || item?.input}: ${statusLabel(item?.status)}`)
          .join('; ');
        enqueueSnackbar(`Некоторые домены исключены: ${details}`, {
          variant: 'warning',
          autoHideDuration: 6000,
          style: { maxWidth: '900px' },
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
      }

      setDomains(validDomains);
      setDomainsValue(validDomains.join('\n'));
      return true;
    } catch (err) {
      enqueueSnackbar('Ошибка проверки доменов. Попробуйте позже.', {
        variant: 'error',
        autoHideDuration: 4000,
        style: { maxWidth: '700px' },
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      return false;
    }
  }, [checkDomains, domainsValue, enqueueSnackbar, parseDomains]);

  const onSave = useCallback(async () => {
    if (!validateSettings() || results.length === 0) {
      return;
    }
    const {
      marketing_image,
      marketing_link,
      custom_utm,
      final_url_suffix,
      business_names,
      ...settingsResult
    } = settings;

    const normalizedBusinessNames = Object.fromEntries(
      Object.entries(business_names || {})
        .filter(([domain]) => domains.includes(domain))
        .map(([domain, value]) => [domain, value.trim()]),
    );

    const filtered =
      settings.mode === 'marketing_image'
        ? { ...settingsResult, business_names: normalizedBusinessNames, custom_utm: custom_utm.trim(), final_url_suffix: final_url_suffix.trim(), marketing_image }
        : { ...settingsResult, business_names: normalizedBusinessNames, custom_utm: custom_utm.trim(), final_url_suffix: final_url_suffix.trim(), marketing_link };

    const res = {
      template_id: activeTemplate.id,
      account_data: results.map(res => {
        return {
          account_id: res.account.id,
          url: 'https://' + res.domain.name,
        };
      }),
      budget_preview: budgetPreviewItems,
      settings: filtered,
    };

    const payload = await templateCampaign(res) as any;
    if (payload.data) {
      enqueueSnackbar(`Создание кампаний добавлено в очередь`, {
        variant: 'success',
        autoHideDuration: 3000,
        style: { maxWidth: '600px' },
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      if (onCreate) {
        onCreate();
      };
    } else {
      enqueueSnackbar(`${payload?.error?.data?.message}`, {
        variant: 'error',
        autoHideDuration: 3000,
        style: { maxWidth: '600px' },
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
    }
  }, [activeTemplate.id, budgetPreviewItems, enqueueSnackbar, onCreate, results, settings, templateCampaign]);

  return (
    <>
      <Typography variant="h3" component="h3">
        Массовое создание кампаний
      </Typography>
      <Stepper activeStep={activeStep} sx={{ mb: 2, mt: 2 }}>
        {steps.map((label, index) => (
          <Step
            key={label}
            sx={{ cursor: 'pointer' }}
            onClick={() => {
              if (activeStep > index) {
                setActiveStep(index);
              }
            }}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <>
          <Stack flex={1}>
            <Select
              fullWidth
              value={template}
              onChange={e => setTemplate(e.target.value as any)}
              disabled={isFetching}
              displayEmpty
              renderValue={(selected) => {
                if (isFetching) {
                  return <Box display="flex" alignItems="center" gap={1}><CircularProgress size={20} /> Загрузка шаблонов...</Box>;
                }
                if (!selected) {
                  return <Typography color="text.secondary">Выберите шаблон</Typography>;
                }
                return selected;
              }}
            >
              <MenuItem value="" disabled>Выберите шаблон</MenuItem>
              {templateData?.data?.map(item => (
                <MenuItem key={item.id} value={item.name}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </Stack>
          <Stack flexDirection="row" mt={2} gap={2}>
            {/* Выбор аккаунтов */}
            <Stack flex={1} sx={{
              maxHeight: '200px !important',
              overflowY: 'auto'
            }}>
              {selectedRows.map((item, index) => (
                <Box key={item.id} display="flex" alignItems="center">
                  <Typography>
                    {index + 1}: {item.name}
                  </Typography>
                </Box>
              ))}
            </Stack>

            {/* Текстовое поле */}
            <Stack flex={1}>
              <Box
                component="textarea"
                sx={{
                  backgroundColor: theme.palette.background.default,
                  border: `1px solid ${theme.palette.divider}`,
                  outline: 'none',
                  // Нативный textarea здесь надежнее autosize-компонента:
                  // высота фиксирована, а длинный список уходит во внутренний скролл.
                  height: '200px',
                  minHeight: '200px',
                  maxHeight: '200px',
                  borderRadius: '6px',
                  lineHeight: '24px',
                  fontFamily: 'inherit',
                  fontSize: theme.typography.body1.fontSize,
                  padding: '8px 12px',
                  width: '100%',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  resize: 'none',
                  boxSizing: 'border-box',
                  display: 'block',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: theme.palette.divider,
                    borderRadius: '8px',
                  },
                }}
                value={domainsValue}
                onChange={e => {
                  const value: string = e.target.value;
                  const parsed = parseDomains(value);
                  setDomainsValue(value);
                  setDomains(parsed);
                }}
                aria-label="maximum height"
                placeholder="Добавьте домены"
              />
            </Stack>
          </Stack>
        </>
      )}
      {activeStep === 1 && (
        <Settings
          settings={settings}
          setSettings={setSettings}
          domains={domains}
          settingsMode={activeTemplate}
          images={images}
          setImages={setImages}
          suffixPreview={suffixPreview}
          suffixPreviewHint={suffixPreviewHint}
        />
      )}
      {activeStep === 2 && (
        <CampaignResults
          results={results}
          images={images}
          settings={settings}
          budgetRange={{
            from: activeTemplate.budget_usd_from,
            to: activeTemplate.budget_usd_to,
            baseCurrency: activeTemplate.budget_base_currency,
          }}
          suffixPreview={suffixPreview}
          suffixPreviewHint={suffixPreviewHint}
        />
      )}
      <Stack flexDirection="row" gap="8px" mt={2}>
        <Button variant="outlined" fullWidth onClick={onCancel}>
          <CloseIcon fontSize="small" sx={{ mr: 1 }} />
          Отмена
        </Button>
        {activeStep > 0 && (
          <Button
            variant="outlined"
            fullWidth
            onClick={() => setActiveStep(prev => Math.max(0, prev - 1))}
            disabled={isCheckingDomains || isLoading}>
            <ArrowBackIcon fontSize="small" sx={{ mr: 1 }} />
            Назад
          </Button>
        )}
        <Button
          disabled={
            (activeStep === 0 && (!template || domains.length === 0)) ||
            isCheckingDomains ||
            isLoading ||
            isPreviewLoading
          }
          variant="contained"
          fullWidth
          onClick={async () => {
            if (activeStep == 0) {
              if (validateDomains()) {
                const ok = await checkDomainsAvailability();
                if (ok) {
                  setActiveStep(activeStep + 1);
                }
              }
            }
            if (activeStep == 1) {
              if (validateSettings()) {
                const generatedResults = handleGenerateCampaign();
                try {
                  const previewPayload = {
                    template_id: activeTemplate.id,
                    account_data: generatedResults.map(item => ({
                      account_id: item.account.id,
                      url: 'https://' + item.domain.name,
                    })),
                  };
                  const previewResponse: any = await previewMultipleCampaignBudget(previewPayload).unwrap();
                  const previewItems: BudgetPreviewItem[] = previewResponse?.data?.items || [];
                  const previewMap = new Map(previewItems.map(item => [item.account_id, item]));
                  setBudgetPreviewItems(previewItems);
                  setResults(generatedResults.map(item => ({
                    ...item,
                    budget_preview: previewMap.get(item.account.id),
                  })));
                  setActiveStep(activeStep + 1);
                } catch (error: any) {
                  enqueueSnackbar(error?.data?.message || 'Не удалось подготовить preview бюджета', {
                    variant: 'error',
                    autoHideDuration: 3000,
                    style: { maxWidth: '600px' },
                    anchorOrigin: { horizontal: 'right', vertical: 'top' },
                  });
                }
              }
            }
            if (activeStep == 2) {
              onSave();
            }
          }}>
          {isLoading || isCheckingDomains || isPreviewLoading ? (
            <CircularProgress size="18px" sx={{ mr: '8px', color: 'white' }} />
          ) : null}
          {!isLoading && !isCheckingDomains && !isPreviewLoading && (
            activeStep === 2
              ? <CheckIcon fontSize="small" sx={{ mr: 1 }} />
              : <ArrowForwardIcon fontSize="small" sx={{ mr: 1 }} />
          )}
          {activeStep == 2
            ? (isLoading ? 'Создание...' : 'Создать')
            : (isCheckingDomains ? 'Проверка домена...' : isPreviewLoading ? 'Подготовка бюджета...' : 'Далее')}
        </Button>
      </Stack>
    </>
  );
};
